import { NotificationInfoOutlineIcon } from '@tableau/icons/NotificationInfoOutlineIcon';
import { Tooltip, TooltipPosition } from '@tableau/widgets';
import React from 'react';

import { beaconComponentRegistryEntry, getUserAccountSettingsURL } from '~src/beacon/BeaconComponentRegistryFunctions';
import { DeepLink } from '~src/beacon/deepLink/DeepLink';
import { SessionServerSelectors, SessionSiteSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { FeatureFlags } from '~src/commonInfra/featureFlags/FeatureFlags';

import { Messages } from '../../codegen/Localize';
import { IServerUserInfo } from '../../userManagement/users/models/IServerUserInfo';
import { ISiteUserInfo } from '../../userManagement/users/models/ISiteUserInfo';
import { UserSettingsStyle } from '../styles/UserSettingsStyle';
import { ChangePassword, ChangePasswordProps } from './ChangePassword';
import { EmailAndDisplayNameForm } from './EmailAndDisplayNameForm';
import { ManageMfaProps, ManageMfaUrl } from './ManageMfa';

const DEEPLINK_ID = 'username';

export interface GeneralAuthSettingsProps {
  //auth families
  isDomainFamilyLocal: boolean;
  isDomainFamilyExternal: boolean;
  isDomainFamilyAD: boolean;
  //data
  emailFormInitialValues: EmailAndDisplayNameForm.FormFields;
  //meta data related to business logic
  updateDisplayNameAllowed: boolean;
  siteUser: ISiteUserInfo;
  serverUser: IServerUserInfo;
  isViewerServerAdmin: boolean;
  isViewerSelf: boolean;
  isUserSelf: boolean;
  changePasswordUrl: string;
  manageMfaUrl: string;
  isViewerAdmin: boolean;
}

function getHeader(){
  return (<h3 className={UserSettingsStyle.titleText}>{Messages.userSettings_Account_Header()}</h3>);
}

export function GeneralAuthSettings({emailFormInitialValues, ...passThroughProps}: GeneralAuthSettingsProps) {
  const emailAndDisplayNameFormProps: EmailAndDisplayNameForm.Props = {
    initialValues: emailFormInitialValues,
    ...passThroughProps,
    handleSubmit: EmailAndDisplayNameForm.handleSubmit
  };
  const changePasswordProps: ChangePasswordProps = {
    ...passThroughProps,

  };
  const manageMfaProps: ManageMfaProps = {
    ...passThroughProps
  };
  const isOnline = SessionServerSelectors.isOnlineEnvironment(getAppStore().getState());
  const isScimAttributesEnabled = SessionSiteSelectors.isScimAttributesEnabled(getAppStore().getState());
  return (
    <>
    {getHeader()}
    <dl className={UserSettingsStyle.settingsContainer}>
      {/* username section */}
      {(passThroughProps.isDomainFamilyAD || passThroughProps.isViewerServerAdmin || passThroughProps.isViewerSelf ||
       ((isOnline && FeatureFlags.usernameEmailDecouplingEnabled && passThroughProps.isViewerAdmin))) &&
        <div className={UserSettingsStyle.settingsSection}>
          <DeepLink deepLinkId={DEEPLINK_ID}>
            <div data-tb-test-id="settings-username" className={UserSettingsStyle.accountSectionContainer}>
              <dt className={UserSettingsStyle.accountSectionLabel}>
                <span>{Messages.username()}</span>
                <Tooltip
                  tooltipContent={Messages.username_toolTip()}
                  position={TooltipPosition.TopRightCorner}
                  followCursor={false}
                  maxWidth={400}
                >
                  <button
                    type='button'
                    aria-label={Messages.moreInfo()}
                    data-tb-test-id='username-tooltip-trigger'
                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}
                  >
                    <NotificationInfoOutlineIcon size={16} alt={''} style={{paddingLeft:'10px'}}/>
                  </button>
                </Tooltip>
              </dt>
              <dd className={UserSettingsStyle.accountSectionValue} tb-test-id="tb-username-value-id">
                {passThroughProps.serverUser.username}
              </dd>
            </div>
          </DeepLink>
        </div>
      }
      <div>
        <EmailAndDisplayNameForm.Form {...emailAndDisplayNameFormProps}/>
      </div>
      {isOnline && isScimAttributesEnabled &&
        <div className={UserSettingsStyle.settingsSection}>
          <div data-tb-test-id="settings-scim-external-id" className={UserSettingsStyle.accountSectionContainer}>
            <dt className={UserSettingsStyle.accountSectionLabel}>
              <span>{Messages.scimExternalIdLabel()}</span>
            </dt>
            <dd className={UserSettingsStyle.accountSectionValue}>
              {passThroughProps.serverUser.scimExternalId}
            </dd>
          </div>
        </div>
      }
    </dl>
    <div className={UserSettingsStyle.settingsContainer}>
      <ChangePassword {...changePasswordProps}/>
    </div>
    <div className={UserSettingsStyle.settingsContainer}>
      <ManageMfaUrl {...manageMfaProps}/>
    </div>
    </>
  );
}

export const UsernameSettingBeaconConfig = beaconComponentRegistryEntry({
  command: () =>  Messages.username(),
  deeplinkId: DEEPLINK_ID,
  url: (props) => getUserAccountSettingsURL(props.sessionInfo),
  isAdmin: false,
  // NOTE: Since we direct users to their own page, passThroughProps.isViewerSelf aka
  // getSessionManagementServiceInstance().isSessionUserSelf(username, domainName) will always be true so, we don't
  // need to check passThroughProps.isDomainFamilyAD (which is
  // ServerSettingsUnauthenticated.isServerDomainFamilyActiveDirectory()) nor passThroughProps.isViewerServerAdmin
  // (which is getSessionManagementServiceInstance().isSessionServerAdmin())
  tags: () => { return [Messages.userSettings_Account_Header()]; }
});